import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Configure, InstantSearch, connectSearchBox } from 'react-instantsearch-dom'
import { ConnectedProps, connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'

import { SearchBar } from '../Search/SearchBar'
import { useUserEatablesSearch } from '../Search/useUserEatablesSearch'

import { SearchJournalHit, eatableHit, motionHit } from './SearchHits'
import { SearchHitsUserEatables } from './SearchHitsUserEatables'

import { BackToTopButton } from 'Components/BackToTopButton'
import TabSwitcher from 'Components/TabSwitcher/TabSwitcher'
import { Food, Recipe, ReduxStoreState } from 'Models'
import { FavoriteTabNames } from 'Pages/Favorites/helpers/FavoriteTabNames'
import { currentWeightSelector } from 'ReduxStore/bodyMeasurements'
import { currentJournalDateSelector } from 'ReduxStore/journalDay'
import { postOrPatchJournalEatableRecordAction } from 'ReduxStore/journalEatableRecords'
import { addJournalMotionRecordAction } from 'ReduxStore/journalMotionRecords'
import { userFoodsSelector } from 'ReduxStore/userFoods'
import { userRecipesSelector } from 'ReduxStore/userRecipes'
import { AlgoliaIndexName, getIndexName, searchClient } from 'Utils/algoliaSearchHelpers'

type SelectorProps = {
  date: Date
  weight: number
  userFoods: Food[]
  userRecipes: Recipe[]
}

const mapStateToProps = createStructuredSelector<ReduxStoreState, SelectorProps>({
  date: currentJournalDateSelector,
  weight: currentWeightSelector,
  userFoods: userFoodsSelector,
  userRecipes: userRecipesSelector
})

const mapDispatchToProps = {
  addJournalEatableRecord: postOrPatchJournalEatableRecordAction,
  addJournalMotionRecord: addJournalMotionRecordAction
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux & {
  onSaveSearchResult: () => void
}

const algoliaIndexForTab = (tab: FavoriteTabNames): AlgoliaIndexName | null => {
  switch (tab) {
    case FavoriteTabNames.FOODS:
      return AlgoliaIndexName.FOODS
    case FavoriteTabNames.RECIPES:
      return AlgoliaIndexName.RECIPES
    case FavoriteTabNames.MOTIONS:
      return AlgoliaIndexName.MOTIONS
    default:
      return null
  }
}

const SyncSearchQuery = connectSearchBox(
  ({
    refine,
    currentRefinement,
    query
  }: {
    refine: (query: string) => void
    currentRefinement: string
    query: string
  }) => {
    useEffect(() => {
      if (query !== currentRefinement) {
        refine(query)
      }
    }, [query, currentRefinement, refine])

    return null
  }
) as React.ComponentType<{ query: string }>

const JournalSearch: FC<Props> = ({
  addJournalEatableRecord,
  addJournalMotionRecord,
  date,
  weight,
  userFoods,
  userRecipes,
  onSaveSearchResult
}: Props) => {
  const [currentTabName, setCurrentTabName] = useState(FavoriteTabNames.FOODS)
  const [searchQuery, setSearchQuery] = useState('')
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { searchResult: userFoodHits, setSearchQuery: setSearchQueryFoods } = useUserEatablesSearch(userFoods)
  const { searchResult: userRecipeHits, setSearchQuery: setSearchQueryRecipes } = useUserEatablesSearch(userRecipes)

  const activeAlgoliaIndex = algoliaIndexForTab(currentTabName)

  const FoodHit = useMemo(
    () => eatableHit({ eatableType: 'FOOD', initialDate: date, addJournalEatableRecord, onSaveSearchResult }),
    [addJournalEatableRecord, date, onSaveSearchResult]
  )

  const RecipeHit = useMemo(
    () => eatableHit({ eatableType: 'RECIPE', addJournalEatableRecord, initialDate: date, onSaveSearchResult }),
    [addJournalEatableRecord, date, onSaveSearchResult]
  )

  const MotionHit = useMemo(() => motionHit({ addJournalMotionRecord, weight, date, onSaveSearchResult }), [
    addJournalMotionRecord,
    date,
    onSaveSearchResult,
    weight
  ])

  const handleQueryChange = useCallback(
    (query: string) => {
      setSearchQuery(query)

      setSearchQueryFoods(query)

      setSearchQueryRecipes(query)
    },
    [setSearchQueryFoods, setSearchQueryRecipes]
  )

  const handleTabSwitch = (tabName: string): void => {
    setCurrentTabName(tabName as FavoriteTabNames)
    scrollContainerRef.current?.scrollTo({ top: 0 })
  }

  const tabs = [
    {
      name: FavoriteTabNames.FOODS,
      title: 'Nahrungsmittel',
      renderContent: (
        <SearchJournalHit hitComponent={FoodHit} hitName={{ singular: 'Nahrungsmittel', plural: 'Nahrungsmittel' }} />
      )
    },
    {
      name: FavoriteTabNames.RECIPES,
      title: 'Rezepte',
      renderContent: <SearchJournalHit hitComponent={RecipeHit} hitName={{ singular: 'Rezept', plural: 'Rezepte' }} />
    },
    {
      name: FavoriteTabNames.USER_EATABLES,
      title: 'Mein Essen',
      renderContent: <></>
    },
    {
      name: FavoriteTabNames.MOTIONS,
      title: 'Aktivitäten',
      renderContent: (
        <SearchJournalHit hitComponent={MotionHit} hitName={{ singular: 'Aktivität', plural: 'Aktivitäten' }} />
      )
    }
  ]

  return (
    <>
      <div className="journal-add-modal-toolbar">
        <SearchBar value={searchQuery} onChangeText={handleQueryChange} />

        <div className="mt-2">
          <TabSwitcher part="tabs" activeTab={currentTabName} onTabSwitch={handleTabSwitch} tabs={tabs} />
        </div>
      </div>

      <div className="journal-add-modal-hits">
        <div ref={scrollContainerRef} className="toolbar-modal-body custom-scrollbar pt-2">
          {activeAlgoliaIndex ? (
            <InstantSearch
              key={activeAlgoliaIndex}
              indexName={getIndexName(activeAlgoliaIndex)}
              searchClient={searchClient}
            >
              <Configure hitsPerPage={50} />
              <SyncSearchQuery query={searchQuery} />
              <TabSwitcher part="content" activeTab={currentTabName} tabs={tabs} />
            </InstantSearch>
          ) : (
            <SearchHitsUserEatables
              foodHits={userFoodHits}
              recipeHits={userRecipeHits}
              initialDate={date}
              addJournalEatableRecord={addJournalEatableRecord}
              onSaveSearchResult={onSaveSearchResult}
            />
          )}
        </div>

        <BackToTopButton scrollContainerRef={scrollContainerRef} />
      </div>
    </>
  )
}

export default connector(JournalSearch)
